/**
 * NotFound — client-side 404 handler for unknown routes.
 *
 * SEO-oriented: emits <meta name="robots" content="noindex, nofollow"> so
 * mistaken URLs stop being canonicalized against the homepage. We can't
 * return a real HTTP 404 without SSR, but marking these pages noindex is
 * the Google-recommended workaround for SPA soft-404s.
 *
 * Visual style matches the existing marketing pages (Landing / Press /
 * Articles) — same white bg, same wordmark, same button family — so we
 * don't introduce a design surface.
 */
import { useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';

const LOGO_URL = "/logo-wordmark.png";

function useNotFoundHead() {
  useEffect(() => {
    const prevTitle = document.title;
    document.title = "Page Not Found | Meet to Keep";

    const setMeta = (name, content, attr = 'name') => {
      let el = document.querySelector(`meta[${attr}="${name}"]`);
      if (!el) {
        el = document.createElement('meta');
        el.setAttribute(attr, name);
        document.head.appendChild(el);
      }
      const prev = el.getAttribute('content');
      el.setAttribute('content', content);
      return { el, prev };
    };

    const robots = setMeta('robots', 'noindex, nofollow');
    const desc   = setMeta('description', 'The page you were looking for could not be found.');

    return () => {
      document.title = prevTitle;
      // Restore prior robots + description so the next route isn't stuck noindexed.
      if (robots.prev != null) robots.el.setAttribute('content', robots.prev);
      else robots.el.remove();
      if (desc.prev != null) desc.el.setAttribute('content', desc.prev);
    };
  }, []);
}

export default function NotFound() {
  const navigate = useNavigate();
  const location = useLocation();
  useNotFoundHead();

  return (
    <div className="min-h-screen bg-white text-[#1A1A1A] flex flex-col" data-testid="not-found-page">
      <nav className="fixed top-0 left-0 right-0 z-50 px-6 py-4 flex items-center justify-between bg-white/90 backdrop-blur-xl border-b border-black/[0.04]">
        <button onClick={() => navigate('/')} aria-label="Meet to Keep home" data-testid="notfound-home-logo">
          <img src={LOGO_URL} alt="Meet to Keep" className="h-10 md:h-12 w-auto object-contain" />
        </button>
      </nav>
      <main className="flex-1 flex items-center justify-center px-6 pt-24 pb-16">
        <div className="max-w-lg w-full text-center">
          <p className="text-[11px] font-semibold uppercase tracking-[0.18em] text-zinc-400 mb-4">Error 404</p>
          <h1 className="text-4xl sm:text-5xl font-semibold tracking-tight leading-[1.05] mb-4" data-testid="notfound-h1">
            Page not found
          </h1>
          <p className="text-base text-zinc-600 leading-relaxed mb-8">
            The link you followed may be broken, or the page may have been moved.
            {location.pathname && (
              <>
                <br />
                <code className="inline-block mt-3 text-xs text-zinc-500 bg-zinc-50 px-2 py-1 rounded">
                  {location.pathname}
                </code>
              </>
            )}
          </p>
          <div className="flex flex-wrap gap-3 justify-center">
            <a href="/" className="inline-flex items-center gap-2 px-5 py-2.5 rounded-full text-sm font-semibold bg-[#1A1A1A] text-white hover:bg-black transition-all" data-testid="notfound-home-btn">
              Return home
            </a>
            <a href="/articles" className="inline-flex items-center gap-2 px-5 py-2.5 rounded-full text-sm font-semibold border border-black/[0.10] text-[#1A1A1A] hover:border-black/[0.25] transition-all" data-testid="notfound-articles-btn">
              Browse articles
            </a>
            <a href="/press" className="inline-flex items-center gap-2 px-5 py-2.5 rounded-full text-sm font-semibold border border-black/[0.10] text-[#1A1A1A] hover:border-black/[0.25] transition-all" data-testid="notfound-press-btn">
              Press &amp; Media
            </a>
          </div>
        </div>
      </main>
    </div>
  );
}
